// cspell:ignore dont
/**
 * In-house dva-core / dva-loading replacement (ENG-4570).
 *
 * Drop-in for the subset of the dva-core API this app uses
 * (`create(hooksAndOpts)` → `{ _models, _store, model, use, start }`). Preserves
 * the model contract (namespace/state/reducers/effects), the `namespace/effect`
 * dispatch API, effect-internal put/take prefixing, the loading slice, and the
 * onReducer/onError/extraReducers/extraEnhancers hooks — so it swaps in behind
 * `createDvaStore` (src/config/state/dva.tsx) and the test store factory
 * (tests/mocks/dvaStore.ts) without touching any model or component.
 *
 * Effects run on a directly-controlled redux-saga@1.x.
 */
import { applyMiddleware, combineReducers, compose, createStore as reduxCreateStore } from 'redux'
import type { StoreEnhancer } from 'redux'
import Plugin, { filterHooks } from './plugin'
import { getReducer, Model, prefixNamespace } from './reducer'
import { createPromiseMiddleware, createSagaMiddleware, getSaga } from './saga'

export { default as createLoading } from './loading'

// dva-core registers this internal bookkeeping model. We keep it for parity so
// the persisted state shape matches the previous dva store — otherwise
// rehydrating a dva-persisted blob logs an "Unexpected key '@@dva'" warning from
// combineReducers (the key has no reducer and gets dropped).
const DVA_MODEL: Model = {
  namespace: '@@dva',
  state: 0,
  reducers: { UPDATE: (state: number) => state + 1 },
}

export interface DvaApp {
  _models: Model[]
  _store: any
  _plugin: Plugin
  use: Plugin['use']
  model: (m: Model) => Model
  start: () => void
}

class DvaAppImpl implements DvaApp {
  _models: Model[] = [prefixNamespace({ ...DVA_MODEL })]

  _store: any = null

  _plugin: Plugin

  private hooksAndOpts: Record<string, any>

  constructor(hooksAndOpts: Record<string, any>) {
    this.hooksAndOpts = hooksAndOpts
    this._plugin = new Plugin()
    this._plugin.use(filterHooks(hooksAndOpts))
  }

  use: Plugin['use'] = (plugin) => this._plugin.use(plugin)

  model = (m: Model): Model => {
    const prefixed = prefixNamespace({ ...m })
    // De-dupe by namespace: re-registering a namespace (e.g. via the HMR
    // workaround in createDvaStore) replaces the existing entry rather than
    // appending, so we never start duplicate effect watchers for one namespace.
    const existingIdx = this._models.findIndex(
      (existing) => existing.namespace === prefixed.namespace,
    )
    if (existingIdx > -1) {
      this._models[existingIdx] = prefixed
    } else {
      this._models.push(prefixed)
    }
    return prefixed
  }

  start = () => {
    const { _plugin: plugin } = this

    // Global error handler — surfaces effect errors via the onError hook(s).
    const onError = (err: any, extension: any) => {
      if (!err) {
        return
      }
      const error = typeof err === 'string' ? new Error(err) : err
      error.preventDefault = () => {
        error._dontReject = true
      }
      plugin.apply('onError', (e: any) => {
        throw new Error(e.stack || e)
      })(error, this._store.dispatch, extension)
    }

    const sagaMiddleware = createSagaMiddleware()
    const promiseMiddleware = createPromiseMiddleware(this)
    const onEffect = plugin.get('onEffect')

    const sagas: Array<() => Iterator<any>> = []
    const reducers: Record<string, any> = {}
    for (const m of this._models) {
      reducers[m.namespace] = getReducer(m.reducers, m.state)
      if (m.effects) {
        sagas.push(getSaga(m.effects, m, onError, onEffect))
      }
    }

    const reducerEnhancer = plugin.get('onReducer')
    const extraReducers = plugin.get('extraReducers')
    const createReducer = () => reducerEnhancer(combineReducers({ ...reducers, ...extraReducers }))

    // Mirror dva-core's store composition: middleware enhancer first, then any
    // extra enhancers (e.g. redux-persist) — order matters for persistence.
    const extraEnhancers: any[] = plugin.get('extraEnhancers')
    const enhancers = [applyMiddleware(promiseMiddleware, sagaMiddleware), ...extraEnhancers]

    // redux 5's variadic `compose` returns `(...args: any[]) => unknown`, which
    // no longer structurally matches `StoreEnhancer`. The chain is built
    // dynamically from plugin hooks (`any[]`), so assert the composed type.
    this._store = reduxCreateStore(
      createReducer(),
      this.hooksAndOpts.initialState || {},
      compose(...enhancers) as StoreEnhancer,
    )

    this._store.runSaga = sagaMiddleware.run

    for (const listener of plugin.get('onStateChange')) {
      this._store.subscribe(() => listener(this._store.getState()))
    }

    sagas.forEach(sagaMiddleware.run)
  }
}

export const create = (hooksAndOpts: Record<string, any> = {}): DvaApp =>
  new DvaAppImpl(hooksAndOpts)
